import type { Thunk, Action } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { FetchAssetParams, ReturnsSettings } from '../types';
import parseStatusCode from '../../errors/parseStatusCode';

export interface ReturnsSettingsModel {
  fetchReturnsSettings: Thunk<ReturnsSettingsModel, FetchAssetParams, Injections, Promise<ReturnsSettings[]>>;
  returnsSettingsActionStart: Action<ReturnsSettingsModel, void>;
  lastAction?: number;
  returnsSettings: Record<string, ReturnsSettings>;
  returnsSettingsFailure: Action<ReturnsSettingsModel, ApplicationErrorOptions>;
  setReturnsSettings: Action<ReturnsSettingsModel, ReturnsSettings[]>;
  linkReturnsSettings: Thunk<
    ReturnsSettingsModel,
    {
      fulfillmentContextId: string;
      returnsSettingsId: string;
    },
    Injections,
    Promise<ReturnsSettings>
  >;
  unlinkReturnsSettings: Thunk<
    ReturnsSettingsModel,
    {
      fulfillmentContextId: string;
      returnsSettingsId: string;
    },
    Injections,
    Promise<ReturnsSettings>
  >;
  createReturnsSettings: Thunk<ReturnsSettingsModel, ReturnsSettings, Injections, Promise<ReturnsSettings>>;
  updateReturnsSettings: Thunk<ReturnsSettingsModel, ReturnsSettings, Injections, Promise<ReturnsSettings>>;
}

export const returnsSettingsModel: ReturnsSettingsModel = {
  returnsSettings: {},
  returnsSettingsFailure: action((state, payload) => {
    console.error(payload);
  }),
  setReturnsSettings: action((state, payload) => {
    for (const val of payload) {
      state.returnsSettings[val.returnsSettingsId] = val;
    }
  }),
  returnsSettingsActionStart: action((state, payload) => {
    state.lastAction = Date.now();
  }),
  fetchReturnsSettings: thunk(async (actions, payload, { injections }) => {
    try {
      actions.returnsSettingsActionStart();
      const res = await injections.assetService.fetchReturnsSettings(payload);
      actions.setReturnsSettings(res);
      return res;
    } catch (error) {
      actions.returnsSettingsFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occurred fetching returns settings!',
        asNotification: true,
      });
    }
  }),
  linkReturnsSettings: thunk(async (actions, payload, { injections }) => {
    try {
      actions.returnsSettingsActionStart();
      const res = await injections.assetService.updateFulfillmentContextReturnSettingsLinking(
        payload.fulfillmentContextId,
        payload.returnsSettingsId,
      );

      return res;
    } catch (error) {
      actions.returnsSettingsFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occurred linking returns settings to fulfillment context!',
        asNotification: true,
      });
    }
  }),
  unlinkReturnsSettings: thunk(async (actions, payload, { injections }) => {
    try {
      actions.returnsSettingsActionStart();
      const res = await injections.assetService.deleteFulfillmentContextReturnSettingsLinking(
        payload.fulfillmentContextId,
        payload.returnsSettingsId,
      );

      return res;
    } catch (error) {
      actions.returnsSettingsFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occurred unlinking returns settings from fulfillment context!',
        asNotification: true,
      });
    }
  }),
  createReturnsSettings: thunk(async (actions, payload, { injections }) => {
    try {
      actions.returnsSettingsActionStart();
      const res = await injections.assetService.createReturnsSettings(payload);
      const newReturnsSettingsId = res.returnsSettingsId;
      //link address
      await injections.assetService.updateReturnsSettingsAddress(newReturnsSettingsId, payload.returnToAddressId);
      //link contact
      await injections.assetService.updateReturnsSettingsContact(newReturnsSettingsId, payload.returnToContactInfoId);
      return res;
    } catch (error) {
      actions.returnsSettingsFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occurred updating returns settings!',
        asNotification: true,
      });
    }
  }),
  updateReturnsSettings: thunk(async (actions, payload, { injections }) => {
    try {
      actions.returnsSettingsActionStart();
      //update returns settings
      const res = await injections.assetService.updateReturnsSettings(payload, payload.returnsSettingsId);
      //link address
      await injections.assetService.updateReturnsSettingsAddress(payload.returnsSettingsId, payload.returnToAddressId);
      //link contact
      await injections.assetService.updateReturnsSettingsContact(
        payload.returnsSettingsId,
        payload.returnToContactInfoId,
      );
      return res;
    } catch (error) {
      actions.returnsSettingsFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occurred updating returns settings!',
        asNotification: true,
      });
    }
  }),
};
